import { ICrop } from "../crops"
import { FieldViewModel } from "../fields"
import { LayerValueType } from "../generated/enums"
import { setDates } from "../utils/date"
import { isDefined } from "../utils/utils"
import { CustomLayerStyle, IDataLayer, IDataLayerType, IDataLayerValue, IGenericPlan } from "./types"

export interface IDatalayersApiLoader {

    loadLayers(layers: IDataLayer[], context: LoaderContext): IDataLayer[];

}

export interface LoaderContext {

    readonly layerTypes: IDataLayerType[]
    readonly customStyles: CustomLayerStyle[]

    readonly crops: ICrop[]
    readonly cropping: FieldViewModel[]

    readonly plans: IGenericPlan[]

}

export type Loader = (layers: IDataLayer[], context: LoaderContext) => IDataLayer[]

export class DatalayersApiLoaderProvider implements ng.IServiceProvider {

    private _loaders: Loader[] = []

    $get() {
        return new DatalayersApiLoader(this._loaders)
    }

    addLoader(loader: (layers: IDataLayer[], context: LoaderContext) => IDataLayer[]) {
        this._loaders.push(loader)
    }

}

export class DatalayersApiLoader implements IDatalayersApiLoader {

    constructor(private _loaders: Loader[]) {
    }

    loadLayers(layers: IDataLayer[], context: LoaderContext): IDataLayer[] {

        setDates(layers, ["CreatedOn", "UpdatedOn", "Date"], false)

        for (const loader of this._loaders) {
            layers = loader(layers, context)
        }

        return layers

    }

}

export function setupLayers(layers: IDataLayer[], context: LoaderContext): IDataLayer[] {

    const { layerTypes, crops, cropping, plans } = context

    // 220908 - Usersnap 2681
    // Add a date conversion step here, as some modules may load layers directly,
    // and call this to set them up
    setDates(layers, ["CreatedOn", "UpdatedOn", "Date"], false)

    layers.forEach(l => {

        l.Type = layerTypes.find(lt => lt.TypeId == l.TypeId)

        const layerCropping = l.CroppingId
            ? cropping.find(c => c.croppingId == l.CroppingId)
            : undefined

        if (crops && (l.CropId || layerCropping)) {
            const cropId = layerCropping?.cropId ?? l.CropId
            l.Crop = crops.find(c => c.CropId == cropId)
        }

        if (layerCropping) {
            l.FirstYearId = layerCropping.yearId
        }

        if (l.Type && l.Zones) {
            setZoneValues(l.Type, l)
        }

        if (plans && l.GenericPlanId) {
            l.GenericPlan = plans.find(p => p.GenericPlanId == l.GenericPlanId)
        }

    })

    setupLayerRelations(layers, layers)

    return layers

}

export function setupLayerRelations(targets: IDataLayer[], allLayers: IDataLayer[]) {

    targets.forEach(l => {

        l.Parent = l.ParentId
            ? allLayers.find(p => p.DataLayerId == l.ParentId)
            : undefined

        l.Children = allLayers.filter(c => c.ParentId == l.DataLayerId)

    })

}

function setZoneValues(type: IDataLayerType, layer: IDataLayer) {

    const zones: { Value: number | IDataLayerValue }[] = layer.Zones

    for (const zone of zones) {

        if (type.ValueType == LayerValueType.Numeric) {

            if (isDefined(zone.Value)) {
                zone.Value = Number(zone.Value)
            }

        }

        else if ((type.ValueType == LayerValueType.Discrete) && type.Values && zone.Value) {

            zone.Value = type.Values.find(v => v.Value == zone.Value) ?? zone.Value

        }

    }

}
